import React, { useEffect } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  withStyles,
  CircularProgress,
  Grid,
  // Paper,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { get, isEmpty } from 'lodash';

import history from 'common/utils/history';
import { Box, Button, Dialog, ReferralInviteBox } from 'components';
import InvestNowDeal from 'views/InvestNow';
import { RootState } from 'redux-modules/store/rootState';
import InvestmentBulk from 'views/InvestmentBulk';
import { Deal } from 'redux-modules/deals/Types';
import { getInvestorDealById, refereeInvestorVisitedDeal } from 'services';
import {
  convertAmountIntoReadableFormat,
  errorMessageHandler,
  formatAmount,
} from 'common/utils/helpers';
import { AdobeIcon, copyUrlIcon } from 'assets';
import { emptyInvestmentDataState } from 'redux-modules/investor/Actions';
import { DEAL_TYPE, STATIC_WEBSITE } from 'common/utils/constants';

import styles from './styles';

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.grey[500],
    fontSize: 14,
    lineHeight: '16px',
    '@media (max-width: 525px)': {
      fontSize: 12.5,
    },
  },
  body: {
    fontSize: 14,
    lineHeight: '16px',
    color: theme.palette.grey[50],
    fontFamily: theme.typography.fontFamily,
    border: 0,
    '@media (max-width: 525px)': {
      fontSize: 12.5,
    },
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.grey.A700,
      borderRadius: 3,
    },
  },
}))(TableRow);

const InvestmentDetails = () => {
  const classes = styles();
  const dispatch = useDispatch();
  const search = history.location.search;
  const spv: any = new URLSearchParams(search).get('spv');
  const env = process.env.REACT_APP_ENV || 'dev';

  const { investor, isLoading } = useSelector(
    ({ Investor }: RootState) => Investor,
  );

  const [investNowModal, setInvestNowModal] = React.useState<boolean>(false);
  const [isDealLoading, setIsDealLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [deal, setDeal] = React.useState<Deal>();
  const [copiedTexts, setCopiedText] = React.useState<string>('Copy Link');

  const copyUrlToClipboard = () => {
    // eslint-disable-next-line no-undef
    navigator.clipboard
      .writeText(`${STATIC_WEBSITE[env]}specific-deal/?deal=${spv}`)
      .then(() => {
        setTimeout(() => setCopiedText('Copied'), 1000);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log('ERROR WHILE COPY URL TO CLIPBOARD:', error);
      });
    setTimeout(() => setCopiedText('Copy Link'), 3000);
  };

  useEffect(() => {
    if (get(history, 'location.state.dealVisited')) {
      refereeInvestorVisitedDeal()
        .then((res: any) => {
          // eslint-disable-next-line no-console
          console.log('RES: DEAL VISITED', res);
        })
        .catch((err: any) => {
          // eslint-disable-next-line no-console
          console.log('ERROR WHILE MARK DEAL VISITED', err);
        });
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(spv) && get(investor, 'reviewed') && isEmpty(deal)) {
      setIsDealLoading(true);
      setErrorMessage('');
      getInvestorDealById({ dealName: spv })
        .then((res: any) => {
          setIsDealLoading(false);
          setDeal(res);
        })
        .catch((err: any) => {
          const message: string = errorMessageHandler(err);
          setErrorMessage(message);
          setIsDealLoading(false);
        });
    }
  }, [spv, investor, deal]);

  return (
    <Box>
      {isEmpty(spv) ? (
        <InvestmentBulk />
      ) : (
        <>
          {isLoading || isDealLoading ? (
            <Box className={classes.loader}>
              <CircularProgress color="inherit" size={40} />
            </Box>
          ) : (
            <>
              <Box className={classes.mainHeader}>
                <Typography variant="h3" className={classes.mainHeaderText}>
                  Investment Details
                </Typography>
                <Box className={classes.mainHeaderBtns}>
                  <Box
                    className={classes.copyUrlBox}
                    onClick={() => copyUrlToClipboard()}
                  >
                    <img
                      src={copyUrlIcon}
                      alt={copyUrlIcon}
                      className={classes.copyUrlIcon}
                    />
                    <Typography variant="h3" className={classes.copyUrlText}>
                      {copiedTexts}
                    </Typography>
                  </Box>
                  {get(investor, 'reviewed') &&
                    !errorMessage &&
                    !get(deal, 'closed') && (
                      <Button
                        name="Invest Now"
                        className={classes.investNowButton}
                        onClick={() => setInvestNowModal(true)}
                      />
                    )}
                </Box>
              </Box>
              {!get(investor, 'reviewed') && (
                <Box className={classes.investDFullBox}>
                  <Typography variant="body1" className={classes.invstTText}>
                    8vdX is reviewing your credentials and you'll be able learn
                    more about investment opportunities once your account is
                    activated by 8vdX. You'll be notified about account
                    activation via email.
                  </Typography>
                </Box>
              )}
              {errorMessage && (
                <Typography variant="body1" className={classes.errorMessage}>
                  {errorMessage}
                </Typography>
              )}
              {isEmpty(errorMessage) && get(investor, 'reviewed') && (
                <>
                  <Box className={classes.investDBox}>
                    <Box className={classes.invstTable}>
                      <Typography
                        variant="body1"
                        className={classes.invstTText}
                      >
                        <b>
                          8vdX SPV for{' '}
                          {get(deal, 'fundTitle') || get(deal, 'deal')}
                        </b>{' '}
                        is open for investments through this portal.
                        <br />
                        Following are the key terms and additional materials:
                      </Typography>
                      {/* <Typography variant="body1" className={classes.invstTSText}>
                        OPPORTUNITY
                      </Typography> */}
                      <TableContainer>
                        <Table
                          className={classes.table}
                          aria-label="customized table"
                        >
                          <TableHead>
                            <TableRow>
                              <StyledTableCell
                                className={classes.invstTh}
                                component="th"
                              >
                                Opportunity
                              </StyledTableCell>
                              <StyledTableCell
                                className={classes.invstTh}
                                component="th"
                              >
                                Details
                              </StyledTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <StyledTableRow>
                              <StyledTableCell
                                className={classes.invstTh}
                                component="th"
                              >
                                {get(deal, 'dealType') === DEAL_TYPE.FUND
                                  ? 'Fund Strategy'
                                  : 'Business Model'}
                              </StyledTableCell>
                              <StyledTableCell>
                                {get(deal, 'businessModel')}
                              </StyledTableCell>
                            </StyledTableRow>
                            <StyledTableRow>
                              <StyledTableCell
                                className={classes.invstTh}
                                component="th"
                              >
                                Geography
                              </StyledTableCell>
                              <StyledTableCell>
                                {get(deal, 'geography')}
                              </StyledTableCell>
                            </StyledTableRow>
                            <StyledTableRow>
                              <StyledTableCell
                                className={classes.invstTh}
                                component="th"
                              >
                                Scheme Capacity
                              </StyledTableCell>
                              <StyledTableCell>
                                {' '}
                                {/* {get(deal, 'schemeCapacity', '-')} */}$
                                {convertAmountIntoReadableFormat(
                                  get(deal, 'schemeCapacity'),
                                )}
                              </StyledTableCell>
                            </StyledTableRow>
                            <StyledTableRow>
                              <StyledTableCell
                                className={classes.invstTh}
                                component="th"
                              >
                                {get(deal, 'dealType') === DEAL_TYPE.FUND
                                  ? 'Manager'
                                  : 'Originator / Manager'}
                              </StyledTableCell>
                              <StyledTableCell>
                                {get(deal, 'manager')}
                              </StyledTableCell>
                            </StyledTableRow>
                            <StyledTableRow>
                              <StyledTableCell
                                className={classes.invstTh}
                                component="th"
                              >
                                Minimum Investment
                              </StyledTableCell>
                              <StyledTableCell>
                                {formatAmount(
                                  get(deal, 'minimumInvestmentAmount', 0),
                                )}
                              </StyledTableCell>
                            </StyledTableRow>
                            <StyledTableRow>
                              {get(deal, 'dealType') === DEAL_TYPE.FUND ? (
                                <>
                                  <StyledTableCell
                                    className={classes.invstTh}
                                    component="th"
                                  >
                                    Management Fees
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {get(deal, 'managementFees', '-')}
                                  </StyledTableCell>
                                </>
                              ) : (
                                <>
                                  <StyledTableCell
                                    className={classes.invstTh}
                                    component="th"
                                  >
                                    Syndication Fees
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {get(deal, 'syndicationFee', '-')}
                                  </StyledTableCell>
                                </>
                              )}
                            </StyledTableRow>
                            <StyledTableRow>
                              {get(deal, 'dealType') === DEAL_TYPE.FUND ? (
                                <>
                                  <StyledTableCell
                                    className={classes.invstTh}
                                    component="th"
                                  >
                                    Performance Fees
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {get(deal, 'performanceFee', '-')}
                                  </StyledTableCell>
                                </>
                              ) : (
                                <>
                                  <StyledTableCell
                                    className={classes.invstTh}
                                    component="th"
                                  >
                                    Servicing Fees
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {get(deal, 'serviceFee', '-')}
                                  </StyledTableCell>
                                </>
                              )}
                            </StyledTableRow>
                            <StyledTableRow>
                              <StyledTableCell
                                className={classes.invstTh}
                                component="th"
                              >
                                Tenure
                              </StyledTableCell>
                              <StyledTableCell>
                                {get(deal, 'tenure', '-')}
                              </StyledTableCell>
                            </StyledTableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                    <Box className={classes.invstHunt}>
                      <Box className={classes.invstHuntBox}>
                        <div className={classes.invstHuntBoxLeft}>
                          <img
                            src={AdobeIcon}
                            alt={AdobeIcon}
                            className={classes.adobeIconImg}
                          />
                        </div>
                        <div>
                          <Typography
                            variant="body1"
                            className={classes.invstHHtext}
                          >
                            <a
                              href={get(deal, 'investmentTeaserLink', '')}
                              target="_blank"
                              rel="noreferrer"
                              className={classes.invstHSText}
                            >
                              Investment Teaser - {get(deal, 'fundTitle')}
                            </a>
                          </Typography>
                          <Typography
                            variant="body1"
                            className={classes.invstHHtext}
                          >
                            {get(deal, 'investmentTeaserLinkDesc')}
                          </Typography>
                        </div>
                      </Box>

                      <Box className={classes.invstHuntBox}>
                        <div className={classes.invstHuntBoxLeft}>
                          <img
                            src={AdobeIcon}
                            alt={AdobeIcon}
                            className={classes.adobeIconImg}
                          />
                        </div>
                        <div>
                          <Typography
                            variant="body1"
                            className={classes.invstHHtext}
                          >
                            <a
                              href={get(deal, 'informationLink', '#')}
                              target="_blank"
                              rel="noreferrer"
                              className={classes.invstHSText}
                            >
                              Information Memorandum - {get(deal, 'fundTitle')}{' '}
                              SPV
                            </a>
                          </Typography>
                          <Typography
                            variant="body1"
                            className={classes.invstHHtext}
                          >
                            {get(deal, 'informationLinkDesc')}
                          </Typography>
                        </div>
                      </Box>

                      <Box className={classes.invstHuntBox}>
                        <div className={classes.invstHuntBoxLeft}>
                          <img
                            src={AdobeIcon}
                            alt={AdobeIcon}
                            className={classes.adobeIconImg}
                          />
                        </div>
                        <div>
                          <Typography
                            variant="body1"
                            className={classes.invstHHtext}
                          >
                            <a
                              href={get(deal, 'subscriptionDocLink', '#')}
                              target="_blank"
                              rel="noreferrer"
                              className={classes.invstHSText}
                            >
                              Subscription docs
                            </a>
                          </Typography>
                          <Typography
                            variant="body1"
                            className={classes.invstHHtext}
                          >
                            {get(deal, 'subscriptionDocDesc')}
                          </Typography>
                        </div>
                      </Box>
                    </Box>
                  </Box>
                  {!get(deal, 'closed') && (
                    <Grid container className={classes.referContainer}>
                      <Grid item md={12}>
                        <Typography
                          variant="h3"
                          className={classes.referFriendText}
                        >
                          Refer a friend and earn 10% of our carry
                        </Typography>
                      </Grid>
                      <Grid item md={12}>
                        <ReferralInviteBox
                          type="DEAL"
                          dealId={get(deal, 'id')}
                        />
                      </Grid>
                    </Grid>
                  )}
                </>
              )}
            </>
          )}
          {/* <Box className={classes.investDBox}>
            <Box className={classes.invstTable}>
              <Typography variant="body1" className={classes.invstTText}>
                <b>8vdX SPV for W22</b> batch is open for investments through
                this portal.
                <br />
                Following are the key terms and additional materials:
              </Typography>
              <TableContainer>
                <Table className={classes.table} aria-label="customized table">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell
                        className={classes.invstTh}
                        component="th"
                      >
                        Opportunity
                      </StyledTableCell>
                      <StyledTableCell>Details</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <StyledTableRow>
                      <StyledTableCell
                        className={classes.invstTh}
                        component="th"
                      >
                        Business Model
                      </StyledTableCell>
                      <StyledTableCell>
                        Bridge funding, Venture debt to startups in YC’s Winter
                        22 batch
                      </StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableCell
                        className={classes.invstTh}
                        component="th"
                      >
                        Geography
                      </StyledTableCell>
                      <StyledTableCell>
                        Global with focus on USA, India, Brazil, UK and
                        Singapore
                      </StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableCell
                        className={classes.invstTh}
                        component="th"
                      >
                        Scheme Capacity
                      </StyledTableCell>
                      <StyledTableCell>$25-30Million</StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableCell
                        className={classes.invstTh}
                        component="th"
                      >
                        Manager
                      </StyledTableCell>
                      <StyledTableCell>
                        Big Tree Finance, Inc. (YC W22)
                      </StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableCell
                        className={classes.invstTh}
                        component="th"
                      >
                        Minimum Investment
                      </StyledTableCell>
                      <StyledTableCell>$50,000</StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableCell
                        className={classes.invstTh}
                        component="th"
                      >
                        Management Fees
                      </StyledTableCell>
                      <StyledTableCell>2% per annum on AUM</StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableCell
                        className={classes.invstTh}
                        component="th"
                      >
                        Performance Fees
                      </StyledTableCell>
                      <StyledTableCell>
                        20% up to MOIC of 3X and 30% for further gains
                      </StyledTableCell>
                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableCell
                        className={classes.invstTh}
                        component="th"
                      >
                        Tenure
                      </StyledTableCell>
                      <StyledTableCell>
                        12 months rolling lock, 5-year side pocket for equity
                        kickers
                      </StyledTableCell>
                    </StyledTableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box className={classes.invstHunt}>
              <Typography variant="body1" className={classes.invstHHtext}>
                <Typography variant="caption" className={classes.invstHSText}>
                  <a
                    href="https://docsend.com/view/i57nptcncsij3zdp"
                    target="_blank"
                    rel="noreferrer"
                    className={classes.invstHSText}
                  >
                    Investment Teaser - YC W22
                  </a>{' '}
                  -
                </Typography>{' '}
                a two-page summary with details such as Investment Structure,
                Terms of YC W22 Bridge Debt program, and Sensitivity of LP
                returns.
              </Typography>
              <Typography variant="body1" className={classes.invstHHtext}>
                <Typography variant="caption" className={classes.invstHSText}>
                  <a
                    href="https://docsend.com/view/huh29y7pjh74cpwj"
                    target="_blank"
                    rel="noreferrer"
                    className={classes.invstHSText}
                  >
                    Information Memorandum - YC W22 SPV
                  </a>{' '}
                  -
                </Typography>{' '}
                legal prospectus of the units offering by YCW22, a series of
                8vdX, LLC.
              </Typography>
              <Typography variant="body1" className={classes.invstHHtext}>
                <Typography variant="caption" className={classes.invstHSText}>
                  <a
                    href="https://docsend.com/view/g9xyk72pcgefhzjg"
                    target="_blank"
                    rel="noreferrer"
                    className={classes.invstHSText}
                  >
                    Subscription docs
                  </a>{' '}
                  -
                </Typography>{' '}
                final drafts that the investors will sign to subscribe to the
                units of the W22 SPV i.e. YCW22, a series of 8vdX LLC. The
                platform will use DocuSign to execute these agreements.
              </Typography>
            </Box>
          </Box> */}
          <Dialog
            open={investNowModal}
            maxWidth={'lg'}
            title=""
            buttonText="submit"
            className={classes.investPopUp}
            handleClose={() => {
              dispatch(emptyInvestmentDataState());
              setInvestNowModal(false);
            }}
          >
            {investNowModal && <InvestNowDeal spv={spv} deal={deal} />}
          </Dialog>
        </>
      )}
    </Box>
  );
};
export default InvestmentDetails;
